// iter147 — Offline & Install page.
//
// Same interaction model as ViewSelectorSheet: two stacked cards, no
// checkboxes, no icons next to titles — the whole card border highlights
// gold when tapped. Only one card can be selected at a time. A single
// fixed-bottom CTA reflects the current selection and triggers the
// corresponding action:
//   • "Save Havana for Offline" → reuses lib/offline.js downloadOfflinePack
//     (same progress steps as Journey's OfflineSavedCard).
//   • "Add to Home Screen" → reuses lib/pwa.js promptInstall (Android
//     native prompt) or navigates to /install for platform-specific
//     guides (iOS Safari 3-step wizard already exists).
//
// The page itself is cacheable by the service worker — HTML navigations
// fall back to the app shell in sw.js so /offline works without a network.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, RotateCcw, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { downloadOfflinePack } from "../lib/offline";
import { api } from "../lib/api";
import { canInstall, promptInstall, subscribeInstall } from "../lib/pwa";

const OFFLINE_KEY = "habano.offline.cached_at";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true
  );
}

function detectPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) return "ios-safari";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function OfflineInstall() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [pending, setPending] = useState(null); // "save" | "install" | null
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ step: "", pct: 0 });
  const [installable, setInstallable] = useState(canInstall());
  const [installed, setInstalled] = useState(isStandalone());
  const platform = detectPlatform();

  useEffect(() => {
    try { setSaved(!!localStorage.getItem(OFFLINE_KEY)); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const unsub = subscribeInstall(setInstallable);
    const handler = () => setInstalled(isStandalone());
    window.addEventListener("appinstalled", handler);
    return () => { unsub(); window.removeEventListener("appinstalled", handler); };
  }, []);

  const runSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      setProgress({ step: t("j71_offline_step_biz"), pct: 15 });
      await api.get("/businesses", { params: { limit: 50 } }).catch(() => {});
      setProgress({ step: t("j71_offline_step_photos"), pct: 40 });
      await Promise.allSettled([
        api.get("/banners").catch(() => {}),
        api.get("/just-for-you", { timeout: 4000 }).catch(() => {}),
      ]);
      setProgress({ step: t("j71_offline_step_fx"), pct: 65 });
      await api.get("/exchange").catch(() => {});
      setProgress({ step: t("j71_offline_step_tips"), pct: 85 });
      // Reuse the canonical offline pack downloader (writes localStorage
      // manifest + queues Mapbox tiles into the service worker).
      await downloadOfflinePack();
      try {
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: "PRECACHE_HAVANA" });
        }
      } catch { /* ignore */ }
      localStorage.setItem(OFFLINE_KEY, new Date().toISOString());
      setProgress({ step: "", pct: 100 });
      setSaved(true);
      toast.success(t("j71_offline_done_toast"));
    } catch (e) {
      toast.error(e?.message || "Could not download");
    } finally {
      setSaving(false);
    }
  };

  const runInstall = async () => {
    // Android Chrome has a native beforeinstallprompt we can call.
    if (installable) {
      const result = await promptInstall();
      if (result === "accepted") setInstalled(true);
      return;
    }
    // Otherwise route to the existing /install wizard (iOS 3-step guide
    // + fallback copy for unsupported browsers).
    nav("/install");
  };

  const ctaLabel = (() => {
    if (!pending) return t("offline_cta_select");
    if (pending === "save") return t("offline_cta_save");
    return t("offline_cta_install");
  })();

  const ctaActive = pending !== null;
  const ctaDisabled = !ctaActive || saving;

  const onCta = () => {
    if (pending === "save") runSave();
    else if (pending === "install") runInstall();
  };

  return (
    <main data-testid="offline-page" className="min-h-screen pb-40" style={{ background: "#0B0B0B" }}>
      <header className="px-5 flex items-start gap-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 28px)" }}>
        <button
          type="button"
          data-testid="offline-back"
          onClick={() => nav(-1)}
          className="grid place-items-center rounded-full hover:bg-white/[0.04] transition-colors"
          style={{ minHeight: 48, minWidth: 48, color: "#F5F1E8", marginLeft: -8 }}
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0 pt-1">
          <h1
            data-testid="offline-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1.15 }}
          >
            {t("offline_page_title")}
          </h1>
          <p
            className="mt-1"
            data-testid="offline-sub"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490", lineHeight: 1.5 }}
          >
            {t("offline_page_sub")}
          </p>
        </div>
      </header>

      <section className="px-5 mt-6 space-y-3" data-testid="offline-cards">
        <OfflineCard
          kind="save"
          title={t("offline_card_save_title")}
          desc={t("offline_card_save_desc")}
          selected={pending === "save"}
          onSelect={() => setPending("save")}
          done={saved}
          doneLabel={t("offline_state_saved")}
          onDoneAction={saved ? runSave : null}
          doneActionLabel={t("offline_state_update")}
          doneActionIcon={RotateCcw}
        />
        <OfflineCard
          kind="install"
          title={t("offline_card_install_title")}
          desc={t("offline_card_install_desc")}
          selected={pending === "install"}
          onSelect={() => setPending("install")}
          done={installed}
          doneLabel={t("offline_state_installed")}
        />
      </section>

      {/* Progress row (only while save is running) */}
      {saving && (
        <div className="px-5 mt-4" data-testid="offline-progress">
          <div style={{ height: 3, background: "#1f1f1f", borderRadius: 999, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${progress.pct}%`,
                background: "#E8B84B",
                transition: "width 200ms ease",
              }}
            />
          </div>
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490",
              textAlign: "center", marginTop: 6,
            }}
          >
            {`${progress.pct}% · ${progress.step}`}
          </div>
        </div>
      )}

      {/* Fixed bottom CTA */}
      <div
        className="fixed left-0 right-0 px-5 pt-4"
        style={{
          bottom: 0,
          background: "#0B0B0B",
          borderTop: "1px solid rgba(255,255,255,0.04)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
          zIndex: 30,
        }}
      >
        <button
          type="button"
          onClick={onCta}
          disabled={ctaDisabled}
          data-testid="offline-cta"
          className="w-full inline-flex items-center justify-center gap-2 transition-opacity disabled:cursor-not-allowed"
          style={ctaActive
            ? {
                height: 52,
                borderRadius: 12,
                background: "linear-gradient(135deg, #E8B84B 0%, #B58E3D 100%)",
                color: "#1a0f06",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
                fontWeight: 700,
                border: "none",
                opacity: ctaDisabled ? 0.6 : 1,
              }
            : {
                height: 52,
                borderRadius: 12,
                background: "transparent",
                border: "1.5px solid rgba(232,184,75,0.3)",
                color: "#6a6a6a",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
                fontWeight: 700,
              }}
        >
          {pending === "save" && !saving && <Download className="w-4 h-4" strokeWidth={1.75} />}
          {pending === "install" && !saving && !installable && platform !== "android" && <Smartphone className="w-4 h-4" strokeWidth={1.75} />}
          {ctaLabel}
        </button>
      </div>

      <BottomNav />
    </main>
  );
}

function OfflineCard({
  kind, title, desc, selected, onSelect,
  done = false, doneLabel = "", onDoneAction = null, doneActionLabel = "", doneActionIcon: DoneIcon = null,
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`offline-card-${kind}`}
      aria-pressed={selected}
      className="text-left w-full transition-colors"
      style={{
        padding: "18px 18px 16px",
        background: selected ? "rgba(232,184,75,0.05)" : "#141414",
        border: selected ? "2px solid #E8B84B" : "2px solid #1f1f1f",
        borderRadius: 8,
      }}
    >
      <h3
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 20,
          color: selected ? "#E8B84B" : "#F5F1E8",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h3>
      <p
        className="mt-2"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          color: selected ? "#B0AAA3" : "#9A9490",
          lineHeight: 1.5,
        }}
      >
        {desc}
      </p>
      {done && (
        <div
          className="mt-3 flex items-center justify-between"
          data-testid={`offline-card-${kind}-done`}
        >
          <span
            className="inline-flex items-center gap-1.5"
            style={{ color: "#7BC97B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
            {doneLabel}
          </span>
          {onDoneAction && (
            <span
              role="link"
              tabIndex={0}
              data-testid={`offline-card-${kind}-update`}
              onClick={(e) => { e.stopPropagation(); onDoneAction(); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault(); e.stopPropagation(); onDoneAction();
                }
              }}
              className="inline-flex items-center gap-1 cursor-pointer"
              style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}
            >
              {DoneIcon && <DoneIcon className="w-3.5 h-3.5" strokeWidth={1.75} />}
              {doneActionLabel}
            </span>
          )}
        </div>
      )}
    </button>
  );
}
